import type { BaseFields } from "@/resources/base";
import type {
  Campaign,
  CampaignResult,
  CampaignWithId,
} from "@/resources/campaign";
import type { Filter } from "@/utils/apiFilter";
import type { Response } from "./apiClient";
import { apiClient } from "./apiClient";

class CampaignService {
  async list(filter: Filter = {}): Promise<CampaignWithId[]> {
    try {
      const response = await apiClient.get<CampaignWithId[]>(
        "/campaigns",
        filter,
      );
      if (response.status === "OK" && response.result) {
        return response.result;
      }
      return [];
    } catch (error) {
      console.error("Failed to list campaigns:", error);
      return [];
    }
  }

  async create(
    campaign: Omit<Campaign, keyof BaseFields>,
  ): Promise<Response<CampaignWithId>> {
    try {
      const response = await apiClient.post<CampaignWithId>(
        "/campaigns",
        campaign,
      );
      if (response.status === "OK" && response.result) {
        return response;
      }
      return response;
    } catch (error) {
      console.error("Failed to create campaign:", error);
      return { status: "ERROR", errorMessage: "An unexpected error occurred" };
    }
  }

  async getByIdOrCode(
    campaignIdOrCode: string,
  ): Promise<CampaignWithId | null> {
    try {
      const response = await apiClient.get<CampaignWithId>(
        `/campaigns/${campaignIdOrCode}`,
      );
      if (response.status === "OK" && response.result) {
        return response.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to get campaign by id or code:", error);
      return null;
    }
  }

  async getConfirmedCouponCounts(
    campaignIds: string[],
  ): Promise<{ campaignId: string; confirmedCount: number }[]> {
    try {
      const response = await apiClient.post<
        { campaignId: string; confirmedCount: number }[]
      >("/campaigns/confirmed-coupon-counts", { campaignIds });
      if (response.status === "OK" && response.result) {
        return response.result;
      }
      return [];
    } catch (error) {
      console.error("Failed to get coupon count:", error);
      return [];
    }
  }

  async updateByIdOrCode(
    campaignIdOrCode: string,
    campaign: Partial<Campaign>,
  ): Promise<Response<CampaignWithId>> {
    try {
      const response = await apiClient.put<CampaignWithId>(
        `/campaigns/${campaignIdOrCode}`,
        campaign,
      );
      if (response.status === "OK" && response.result) {
        return response;
      }
      return response;
    } catch (error) {
      console.error("Failed to update campaign:", error);
      return { status: "ERROR", errorMessage: "An unexpected error occurred" };
    }
  }

  async deleteByIdOrCode(campaignIdOrCode: string): Promise<boolean> {
    try {
      const response = await apiClient.delete(`/campaigns/${campaignIdOrCode}`);
      return response.status === "OK";
    } catch (error) {
      console.error("Failed to delete campaign:", error);
      return false;
    }
  }

  async createManyForPackage(
    packageId: string,
    campaigns: Omit<Campaign, keyof BaseFields>[],
  ): Promise<CampaignWithId[]> {
    try {
      const response = await apiClient.post<CampaignWithId[]>(
        `/packages/${packageId}/campaigns`,
        { campaigns },
      );
      if (response.status === "OK" && response.result) {
        return response.result;
      }
      return [];
    } catch (error) {
      console.error("Failed to batch create campaigns:", error);
      return [];
    }
  }

  async getCampaignResults(
    campaignCode: string,
  ): Promise<CampaignResult[] | null> {
    try {
      const campaignResults = await apiClient.get<CampaignResult[]>(
        `/campaigns/${campaignCode}/campaign-results`,
      );
      if (campaignResults.status === "OK" && campaignResults.result) {
        return campaignResults.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to get campaign results:", error);
      return null;
    }
  }
}

export const campaignService = new CampaignService();
